// =====================================================
// EVENT SESSIONS HOOK
// React Query hook for managing event sessions
// =====================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import {
  getEventSessions,
  createEventSession,
  updateEventSession,
  deleteEventSession,
} from '@/Services/eventSessions.service';
import type { EventSessionInsert, EventSessionUpdate } from '@/types/event.types';

// ===================
// QUERY KEYS
// ===================

export const eventSessionsKeys = {
  all: ['eventSessions'] as const,
  byEvent: (eventId: string) => [...eventSessionsKeys.all, eventId] as const,
};

// ===================
// HOOKS
// ===================

export function useEventSessions(eventId: string) {
  return useQuery({
    queryKey: eventSessionsKeys.byEvent(eventId),
    queryFn: () => getEventSessions(eventId),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSessionMutations(eventId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: eventSessionsKeys.byEvent(eventId) });

  const create = useMutation({
    mutationFn: (session: EventSessionInsert) => createEventSession(session),
    onSuccess: () => {
      invalidate();
      toast({ title: 'Sesión creada exitosamente' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error al crear sesión', description: error.message, variant: 'destructive' });
    },
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: EventSessionUpdate }) =>
      updateEventSession(id, data),
    onSuccess: () => {
      invalidate();
      toast({ title: 'Sesión actualizada exitosamente' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error al actualizar sesión', description: error.message, variant: 'destructive' });
    },
  });

  const remove = useMutation({
    mutationFn: deleteEventSession,
    onSuccess: () => {
      invalidate();
      toast({ title: 'Sesión eliminada exitosamente' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error al eliminar sesión', description: error.message, variant: 'destructive' });
    },
  });

  return {
    createSession: create.mutateAsync,
    updateSession: update.mutateAsync,
    deleteSession: remove.mutateAsync,
    isCreating: create.isPending,
    isUpdating: update.isPending,
    isDeleting: remove.isPending,
    isMutating: create.isPending || update.isPending || remove.isPending,
  };
}
